// @flow
import { Record } from "immutable"

import type { Passengers } from "booking/services/reservation/types"

// in case of adding new fields update also TRANS_KEY_SINGLE and PassengersCount.keysAvailable
const PassengersCountRecord = Record({
  adults: 1,
  children: 0,
  infants: 0,
})

const cleanInst = new PassengersCountRecord()

const TRANS_KEY_SINGLE = {
  adults: {
    one: "adult",
    many: "adults",
  },
  children: {
    one: "child",
    many: "children",
  },
  infants: {
    one: "infant",
    many: "infants",
  },
}

const TRANS_KEY_MULTI = "passengers"

const MAX_COUNT = 9

class PassengersCount extends PassengersCountRecord<string, number> {
  // eslint-disable-next-line flowtype/no-weak-types
  constructor(data: Object = {}) {
    let cleanData = {}

    let sum = 0

    PassengersCount.keysAvailable.forEach(key => {
      let value = parseInt(data[key], 10)

      if (isNaN(value) || value < 0) {
        value = 0
      } else {
        if (key === "infants" && value > cleanData.adults) {
          value = cleanData.adults
        }

        if (sum + value > MAX_COUNT) {
          value = MAX_COUNT - sum
        }
      }

      cleanData[key] = value
      sum += value
    })

    if (sum === 0) {
      cleanData = {}
    }

    super(cleanData)
  }

  static fromPassengers(passengers: Passengers): PassengersCount {
    return new PassengersCount({
      adults: passengers.adult,
      children: passengers.child,
      infants: passengers.infant,
    })
  }

  isMulti(): boolean {
    return PassengersCount.keysAvailable.filter(key => this.get(key) > 0).length > 1
  }

  isPristine(): boolean {
    return this.equals(cleanInst)
  }

  isValid(key: string, newValue: number): boolean {
    if (this.merge({ [key]: newValue }).getSum() > MAX_COUNT) {
      return false
    }

    if (newValue < (key === "adults" ? 1 : 0)) {
      return false
    }

    if (key === "infants" && newValue > this.get("adults")) {
      return false
    }

    return true
  }

  getSum(): number {
    return PassengersCount.keysAvailable.reduce((prev, key) => prev + this.get(key), 0)
  }

  getTransKey(): string {
    if (this.isMulti()) {
      return TRANS_KEY_MULTI
    }

    const keyWithValue = PassengersCount.keysAvailable.find(key => this.get(key) > 0)

    const source = TRANS_KEY_SINGLE[keyWithValue]

    return this.get(keyWithValue) > 1 ? source.many : source.one
  }

  set(key: string, value: number): PassengersCount {
    const result = super.set(key, value)

    if (key === "adults" && result.get("adults") < result.get("infants")) {
      return result.set("infants", value)
    }

    return result
  }
}

PassengersCount.keysAvailable = ["adults", "children", "infants"]

export default PassengersCount
